import { useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";

interface UseUrlSearchOptions<T> {
  defaultValues: T;
  defaultPage?: number;
  defaultSize?: number;
}

type QueryParams<T> = Partial<T & { page: number; size: number }>;

/**
 * Hook to manage search filters and pagination in the URL
 */
export function useUrlSearch<T extends object>({
  defaultValues,
  defaultPage = 1,
  defaultSize = 10,
}: UseUrlSearchOptions<T>) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Check if URL has any filter params (exclude pagination)
  const hasFiltersInUrl = useMemo(() => {
    const filterKeys = Object.keys(defaultValues);
    return filterKeys.some((key) => searchParams.has(key));
  }, [searchParams, defaultValues]);

  // On phones the filter panel is a full-screen sheet — auto-opening it just
  // because the URL has filter params would block the page on every refresh.
  const isMobileViewport = () =>
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(max-width: 767px)").matches;

  const [showFilters, setShowFilters] = useState(
    hasFiltersInUrl && !isMobileViewport(),
  );

  // Parse values from URL
  const appliedValues = useMemo(() => {
    const values = { ...defaultValues } as Record<string, any>;
    const defaults = defaultValues as Record<string, any>;

    Object.keys(defaults).forEach((key) => {
      const urlValue = searchParams.get(key);
      if (urlValue !== null) {
        values[key] = parseValue(urlValue, defaults[key]);
      }
    });

    return values as T;
  }, [searchParams, defaultValues]);

  // Pagination from URL
  const page = Number(searchParams.get("page")) || defaultPage;
  const size = Number(searchParams.get("size")) || defaultSize;

  // Draft values for form inputs
  const [draftValues, setDraftValues] = useState<T>(appliedValues);

  // Keep draft in sync with applied values when URL changes
  useEffect(() => {
    setDraftValues(appliedValues);
  }, [appliedValues]);

  // Auto-show filters when URL has filter params — desktop only; on mobile the
  // sheet must stay closed until the user explicitly taps the filter toggle.
  useEffect(() => {
    if (hasFiltersInUrl && !isMobileViewport()) {
      setShowFilters(true);
    }
  }, [hasFiltersInUrl]);

  /**
   * Updates the URL with new parameters
   */
  const updateUrl = useCallback(
    (newParams: QueryParams<T>) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          const defaults = defaultValues as Record<string, unknown>;

          Object.entries(newParams).forEach(([key, value]) => {
            const isDefaultPagination =
              (key === "page" && value === defaultPage) ||
              (key === "size" && value === defaultSize);

            const isEmpty =
              value === null ||
              value === undefined ||
              value === "" ||
              (Array.isArray(value) && value.length === 0);

            const isDefault =
              key in defaults && isEqualToDefault(value, defaults[key]);

            const defaultVal = defaults[key];
            const defaultIsNonEmpty =
              key in defaults &&
              defaultVal !== null &&
              defaultVal !== undefined &&
              defaultVal !== "" &&
              !(Array.isArray(defaultVal) && defaultVal.length === 0);

            if (isDefaultPagination || isDefault) {
              // Value matches default - remove from URL (will use default)
              params.delete(key);
            } else if (isEmpty && defaultIsNonEmpty) {
              // User explicitly cleared a field that has a non-empty default
              // Store empty string to indicate "cleared"
              params.set(key, "");
            } else if (isEmpty) {
              // Empty value with empty default - just delete
              params.delete(key);
            } else {
              let formattedValue: string;
              if (value instanceof Date) {
                formattedValue = value.toISOString();
              } else if (Array.isArray(value)) {
                formattedValue = value.join(",");
              } else {
                formattedValue = String(value);
              }
              params.set(key, formattedValue);
            }
          });

          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams, defaultPage, defaultSize, defaultValues],
  );

  const applySearch = useCallback(() => {
    updateUrl({ ...draftValues, page: 1 });
  }, [draftValues, updateUrl]);

  const resetSearch = useCallback(() => {
    setDraftValues(defaultValues);
    updateUrl({
      ...defaultValues,
      page: 1,
      size: defaultSize,
    } as QueryParams<T>);
  }, [defaultValues, updateUrl, defaultSize]);

  const updateDraft = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setDraftValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const setPage = useCallback(
    (newPage: number) => {
      updateUrl({ page: newPage } as QueryParams<T>);
    },
    [updateUrl],
  );

  const setSize = useCallback(
    (newSize: number) => {
      updateUrl({ size: newSize, page: 1 } as QueryParams<T>);
    },
    [updateUrl],
  );

  return {
    appliedValues,
    draftValues,
    updateDraft,
    applySearch,
    resetSearch,
    page,
    size,
    setPage,
    setSize,
    showFilters,
    setShowFilters,
  };
}

/**
 * Helper to check if value equals its default (handles Date comparison)
 */
function isEqualToDefault(value: unknown, defaultVal: unknown): boolean {
  if (value instanceof Date && defaultVal instanceof Date) {
    return value.getTime() === defaultVal.getTime();
  }
  if (Array.isArray(value) && Array.isArray(defaultVal)) {
    return (
      value.length === defaultVal.length &&
      value.every((v, i) => v === defaultVal[i])
    );
  }
  return value === defaultVal;
}

/**
 * Helper to parse URL string values back to their expected types
 */
function parseValue(urlValue: string, defaultValue: unknown): any {
  // Handle explicit empty string - user cleared a field with non-empty default
  if (urlValue === "") {
    // Return null for Date fields, empty string for others
    if (defaultValue instanceof Date) {
      return null;
    }
    // For arrays, return empty array
    if (Array.isArray(defaultValue)) {
      return [];
    }
    return "";
  }

  // Handle arrays: if default is an array, parse comma-separated string back to array
  if (Array.isArray(defaultValue)) {
    const items = urlValue.split(",");
    // Check if array items should be numbers (based on default array content or empty number array)
    const shouldBeNumbers =
      defaultValue.length === 0 ||
      defaultValue.every((item) => typeof item === "number");
    if (shouldBeNumbers) {
      return items.map((item) => Number(item)).filter((n) => !isNaN(n));
    }
    return items;
  }

  if (typeof defaultValue === "number") {
    const num = Number(urlValue);
    return isNaN(num) ? defaultValue : num;
  }

  if (typeof defaultValue === "boolean") {
    return urlValue === "true";
  }

  // Handle dates: default is Date, null, or empty string with ISO date in URL
  if (
    defaultValue instanceof Date ||
    (defaultValue === null && isISODateString(urlValue)) ||
    (defaultValue === "" && isISODateString(urlValue))
  ) {
    const date = new Date(urlValue);
    return isNaN(date.getTime())
      ? defaultValue instanceof Date
        ? null
        : urlValue
      : date;
  }

  if (defaultValue === null) {
    if (/^-?\d+$/.test(urlValue)) return Number(urlValue);
    if (urlValue === "true" || urlValue === "false") return urlValue === "true";
  }

  return urlValue;
}

function isISODateString(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?)?$/.test(value);
}
